import React, { useLayoutEffect } from 'react';
import oneAppModuleWrapper from '@americanexpress/one-app-module-wrapper';
import { loadLanguagePack } from '@americanexpress/one-app-ducks';
import PropTypes from 'prop-types';
import { compose } from 'redux';
import { connect } from 'react-redux';
import Helmet from 'react-helmet';
import { RenderModule, loadModule } from 'holocron';
import SharedScripts, { GtagWrapper } from '@americanexpress/shared-scripts';
import { IntlProvider } from 'react-intl';
import childRoutes from '../childRoutes';
import DataLayer from './DataLayer';
import { mapDispatchToProps, reducer } from '../ducks';
import {
  onLoadInAuth,
  onLoadBiocatch,
  showHeaderFooter,
  relay42SiteIds,
  shouldLoadRelay42,
  loadBioCatch,
  loadGtagScript,
} from '../utils';
import OfflineWarning from './OfflineWarning';
import NavBarHeader from './NavBarHeader';
import RenderInavHeader from './RenderInavHeader';
import { GLOBAL_HEADER_NAME, GLOBAL_FOOTER_NAME } from '../utils/constants';

export const useDisableIntl = () => {
  useLayoutEffect(() => {
    // eslint-disable-next-line no-underscore-dangle -- accessing Intl internal to restore RegExp behavior
    Intl.__disableRegExpRestore();
  });
};

export const AxpProspectRoot = ({
  children,
  locale,
  actSetInAuthUUID,
  actSetBiocatchUUID,
  actSetUpdateDdlView,
  snrData,
  journeyType,
  location,
  languageData,
  actSetInavData,
}) => {
  useDisableIntl();
  const renderNewHeader = () => {
    if (locale.toLowerCase() === 'fr-fr') {
      return <NavBarHeader />;
    }
    return null;
  };
  return (
    <>
      <Helmet
        titleTemplate="American Express - %s"
        htmlAttributes={{ lang: locale }}
        meta={[
          { 'http-equiv': 'X-UA-Compatible', content: 'IE=edge' },
          { charset: 'utf-8' },
          { name: 'viewport', content: 'width=device-width, initial-scale=1.0' },
          { content: 'noindex, nofollow', name: 'robots' },
        ]}
        link={[{ rel: 'icon', href: 'https://www.americanexpress.com/favicon.ico' }]}
      />
      <SharedScripts
        name="inauth"
        windowCheck="inauthSetTid"
        version="^1.1.0"
        onload={onLoadInAuth(actSetInAuthUUID, location)}
      />
      {loadBioCatch(locale) ? (
        <SharedScripts
          name="biocatch"
          windowCheck="cdApi"
          version="^2.23.1"
          onload={onLoadBiocatch(actSetBiocatchUUID)}
        />
      )
        : ''}
      <SharedScripts name="contentsquare" version="^1.0.0" />
      <SharedScripts name="ensighten" version="^0.1.1" />
      <SharedScripts name="oysterplus" version="^1.0.0" />
      {loadGtagScript(locale) && (<GtagWrapper version="^1.0.0" />)}

      <IntlProvider locale={locale} messages={languageData}>
        {shouldLoadRelay42(locale) ? (
          <SharedScripts
            name="relay42"
            windowCheck="_st"
            version="^1.0.0"
            sideEffect={() => {
              const siteId = relay42SiteIds[locale];

              if (typeof global.scriptConfig !== 'object') {
                global.scriptConfig = {};
              }

              global.scriptConfig.relay42 = {
                siteId,
              };
            }}
          />
        )
          : ''}
        {showHeaderFooter({
          snrData,
          locale,
          journeyType,
          location,
        }) ? (
          <>
            <DataLayer
              locale={locale}
              location={location}
              actSetUpdateDdlView={actSetUpdateDdlView}
            >
              {renderNewHeader()}
              {locale?.toLowerCase() !== 'en-au' && !(locale?.toLowerCase() === 'fr-fr')
                ? (
                  <RenderInavHeader
                    locale={locale?.toLowerCase()}
                    location={location}
                    languageData={languageData}
                    actSetInavData={actSetInavData}
                    childModuleProps={children?.props?.route}
                  />
                ) : null}
              <OfflineWarning />
              {children}
            </DataLayer>
            <RenderModule moduleName="axp-footer" />
          </>
          ) : (
            <DataLayer locale={locale} actSetUpdateDdlView={actSetUpdateDdlView}>
              <OfflineWarning />
              {children}
            </DataLayer>
          )}
      </IntlProvider>
    </>
  );
};

AxpProspectRoot.childRoutes = childRoutes;

export const mapStateToProps = (state) => {
  const snrData = state.getIn(['modules', 'axp-retrieve-application', 'appData']);
  const journeyType = state.getIn(['modules', 'axp-prospect-root', 'postBody', 'journeyType']);
  return { snrData, journeyType };
};

export const loadModuleData = async ({ store }) => {
  const { dispatch } = store;
  await dispatch(loadModule(GLOBAL_HEADER_NAME));
  await dispatch(loadModule(GLOBAL_FOOTER_NAME));
  await dispatch(loadLanguagePack('axp-prospect-root', { fallbackLocale: 'en-US' }));
};

AxpProspectRoot.holocron = {
  name: 'axp-prospect-root',
  loadModuleData,
  reducer,
};

if (!global.BROWSER) {
  // eslint-disable-next-line n/global-require -- conditional server-side require for appConfig
  AxpProspectRoot.appConfig = require('../appConfig').default;
}
const hocChain = compose(
  oneAppModuleWrapper('axp-prospect-root'),
  connect(mapStateToProps, mapDispatchToProps)
);

AxpProspectRoot.propTypes = {
  children: PropTypes.oneOfType([PropTypes.node, PropTypes.element]).isRequired,
  snrData: PropTypes.objectOf(PropTypes.string),
  locale: PropTypes.string.isRequired,
  actSetInAuthUUID: PropTypes.func.isRequired,
  actSetBiocatchUUID: PropTypes.func.isRequired,
  actSetUpdateDdlView: PropTypes.func.isRequired,
  journeyType: PropTypes.string,
  languageData: PropTypes.objectOf(PropTypes.string),
  location: PropTypes.shape({
    pathname: PropTypes.string,
  }).isRequired,
  actSetInavData: PropTypes.func,
};

export default hocChain(AxpProspectRoot);
